<template>
  <div :style="CSS_VARS" class="ecs-avatar">
    <v-avatar :size="size" :style="AVATAR_STYLES">
      <transition mode="out-in" :name="noAnimation ? '' : 'avatar'">
        <img v-if="avatar" id="ecs-avatar-avatar" :src="avatar" class="ecs-avatar-image" :alt="alt" />
        <span v-else-if="initials" id="ecs-avatar-initials">{{ initials }}</span>
        <v-icon v-else id="ecs-avatar-account">mdi-account</v-icon>
      </transition>
    </v-avatar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Contact from '@/classes/Contact';

export default {
  name: 'EcsAvatar',

  props: {
    size: {
      type: Number,
      default: 40
    },
    contactId: {
      type: String,
      default: null
    },
    agentId: {
      type: String,
      default: null
    },
    alt: {
      type: String,
      default: null
    },
    borderWidth: {
      type: Number,
      default: 0
    },
    backgroundColor: {
      type: String,
      default: null
    },
    noAnimation: {
      type: Boolean,
      default: false
    },
    manualRequest: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      borderColor: 'transparent'
    };
  },

  watch: {
    agentId() {
      this.registerAvatarConsumer({ agentId: this.agentId });
    },
    contactId() {
      this.registerAvatarConsumer({ contactId: this.contactId });
    }
  },

  created() {
    if ((this.agentId || this.contactId) && !this.embeddedAvatar) {
      this.registerAvatarConsumer({
        agentId: this.agentId,
        contactId: this.contactId,
        manualRequest: this.manualRequest
      });
    }
  },

  beforeDestroy() {
    if ((this.agentId || this.contactId) && !this.embeddedAvatar) {
      this.unregisterAvatarConsumer({ agentId: this.agentId, contactId: this.contactId });
    }
  },

  computed: {
    ...mapGetters(['getContact', 'getContactAvatar', 'getAgentAvatar']),

    contact() {
      return this.contactId ? this.getContact(this.contactId) : {};
    },

    embeddedAvatar() {
      return this.contact && this.contact.avatar !== undefined;
    },

    avatar() {
      if (this.embeddedAvatar) {
        return this.contact.avatar;
      } else if (this.agentId || this.contactId) {
        return this.agentId ? this.getAgentAvatar(this.agentId) : this.getContactAvatar(this.contactId);
      } else {
        return null;
      }
    },

    initials() {
      return this.contact && this.contact.initials ? this.contact.initials : Contact.getInitials(this.alt);
    },

    AVATAR_STYLES() {
      let backgroundColor = 'var(--v-secondary)';
      let fontColor = 'var(--v-secondary-text-color)';

      if (this.initials !== '') {
        backgroundColor = this.getColorFromString(this.initials);
        fontColor = this.getFontColorFromString(this.initials);
      }

      return {
        backgroundColor,
        color: fontColor,
        fontSize: this.size < 20 ? '8px' : `${this.size / 2.7}px`,
        lineHeight: `${this.size}px`
      };
    },

    CSS_VARS() {
      return {
        '--size': `${this.size + this.borderWidth * 2}px`,
        '--border-width': `${this.borderWidth}px`,
        '--border-color': this.borderColor || 'transparent'
      };
    }
  },

  methods: {
    ...mapActions(['registerAvatarConsumer', 'unregisterAvatarConsumer']),

    getHueFromString(string = '') {
      let hash = 0;
      for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 20) - hash);
      }
      return hash % 360;
    },

    getColorFromString(string) {
      const saturation = 65;
      const lightness = 70;
      const hue = this.getHueFromString(string);
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    },

    getFontColorFromString(string) {
      const saturation = 65;
      const lightness = 32;
      const hue = this.getHueFromString(string);
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    }
  }
};
</script>

<style scoped>
.ecs-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--size);
  height: var(--size);
  border: var(--border-width);
  border-style: solid;
  border-radius: 50%;
  border-color: var(--border-color);
  font-size: var(--font-size);
}

.avatar-enter-active {
  animation: scale-in 250ms;
}

.avatar-leave-active {
  animation: scale-in 250ms reverse;
}

/* CSS Animation Keyframes */
@keyframes scale-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
